import { gql, useQuery } from '@apollo/client';
import IconBulkEdit from '@apollo/icons/default/IconBulkEdit.svg';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconClients from '@apollo/icons/default/IconClients.svg';
import IconCloud from '@apollo/icons/default/IconCloud.svg';
import IconDiff from '@apollo/icons/default/IconDiff.svg';
import IconDoubleChevronLeft from '@apollo/icons/default/IconDoubleChevronLeft.svg';
import IconDoubleChevronRight from '@apollo/icons/default/IconDoubleChevronRight.svg';
import IconExplorer from '@apollo/icons/default/IconExplorer.svg';
import IconMetrics from '@apollo/icons/default/IconMetrics.svg';
import IconProposals from '@apollo/icons/default/IconProposals.svg';
import IconRocket from '@apollo/icons/default/IconRocket.svg';
import IconSchema from '@apollo/icons/default/IconSchema.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconServices from '@apollo/icons/default/IconServices.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import IconTelescope4 from '@apollo/icons/default/IconTelescope4.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import { Button, Spinner } from '@apollo/orbit';
import classnames from 'classnames';
import { LocationDescriptorObject } from 'history';
import React, { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useGraphVisibility } from 'src/app/graph/hooks/useGraphVisibility';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import {
  changelogRouteConfig,
  checkRouteConfig,
  checksRouteConfig,
  clientsRouteConfig,
  cloudSettingsRouteConfig,
  graphSettingsRouteConfig,
  insightsRouteConfig,
  launchesRouteConfig,
  sandboxCheckRouteConfig,
  sandboxDiffRouteConfig,
  sandboxGraphRouteConfig,
  subgraphsRouteConfig,
  variantHomepageRouteConfig,
  variantPersistedQueriesRouteConfig,
  variantProposalListRouteConfig,
  variantRouteConfig,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { InformationalNudge } from 'src/components/informationalNudge/InformationalNudge';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { ThemeProvider } from 'src/components/themeProvider/ThemeProvider';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { GraphType } from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { AnyRouteConfig, RouteConfig } from 'src/lib/routeConfig/RouteConfig';

export const graphNavQuery = gql`
  query GraphNavQuery($id: ID!, $graphVariant: String!) {
    service(id: $id) {
      id
      graphType
      variant(name: $graphVariant) {
        id
        isContract
        hasManagedSubgraphs
      }
      account {
        id
        name
      }
    }
  }
`;

export const CollapsibleNavItem = React.forwardRef(
  <ExtraMatchRoutes extends AnyRouteConfig[]>(
    {
      label,
      collapsed,
      onClick,
      to,
      disabled,
      icon: Icon,
      tertiary,
      extraActiveRoutes,
      withFeatureAdvertisementDot,
    }: {
      label: string;
      collapsed: boolean;
      disabled?: boolean;
      icon: typeof IconSearch;
      tertiary?: boolean;
      withFeatureAdvertisementDot?: boolean;
      to?: LocationDescriptorObject<unknown>;
      onClick?: () => void;
      extraActiveRoutes?: ExtraMatchRoutes;
    },
    ref:
      | ((instance: HTMLButtonElement | HTMLAnchorElement | null) => void)
      | React.RefObject<HTMLButtonElement | HTMLAnchorElement>
      | null
      | undefined,
  ) => {
    const location = useLocation();
    const shouldShowVariantLevelPersistedQueries = useLDFlag(
      'gov-pq-ux-sort-filter-delete',
    );
    const match =
      to && to.pathname ? matchPath(location.pathname, to.pathname) : false;
    const isActive = !!(
      match ||
      extraActiveRoutes?.some((route) => {
        return matchPath(location.pathname, {
          path: route.definition,
          exact: false,
        });
      })
    );

    const icon = (
      <Icon className={classnames(tertiary ? 'size-3' : 'size-5')} />
    );
    const props = {
      children: <>{collapsed ? icon : label}</>,
      className: classnames(
        'relative h-[42px]',
        tertiary && 'uppercase tracking-widest',
        collapsed
          ? 'w-[42px]'
          : shouldShowVariantLevelPersistedQueries
            ? 'w-[168px] justify-start'
            : 'w-32 justify-start',
        isActive && 'bg-btn-secondary-selected',
      ),
      isDisabled: disabled,
      variant: 'hidden' as const,
      leftIcon: collapsed ? undefined : icon,
      size: tertiary ? ('xs' as const) : ('sm' as const),
    };

    return (
      <Tooltip
        placement="right"
        shouldWrapChildren={disabled}
        label={
          disabled ? (
            <>
              You do not have permission to access the <b>{label}</b> page.
            </>
          ) : (
            label
          )
        }
      >
        {!!to && !disabled ? (
          <Button
            as={Link}
            to={to}
            {...props}
            ref={ref as (instance: HTMLAnchorElement | null) => void}
          >
            {withFeatureAdvertisementDot && (
              <FeatureAdvertisementDot collapsed={collapsed} />
            )}
            {props.children}
          </Button>
        ) : (
          <Button
            onClick={onClick}
            {...props}
            ref={ref as (instance: HTMLButtonElement | null) => void}
          >
            {withFeatureAdvertisementDot && (
              <FeatureAdvertisementDot collapsed={collapsed} />
            )}
            {props.children}
          </Button>
        )}
      </Tooltip>
    );
  },
);

const FeatureAdvertisementDot = ({ collapsed }: { collapsed: boolean }) => (
  <div
    className={classnames(
      'absolute size-2 rounded bg-notice',
      collapsed ? '-right-1 -top-1' : 'right-1',
    )}
  />
);

interface Props {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  wrapper?: { wrapperElement: React.ReactElement; wrapperOn: boolean };
}

export const GraphNav = ({ collapsed, setCollapsed }: Props) => {
  const location = useLocation();
  const graphRef = useGraphRef();

  const [currentAccountId] = useCurrentAccountId();
  const currentPlanResult = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const { data, loading, error } = useQuery<
    GraphQLTypes.GraphNavQuery,
    GraphQLTypes.GraphNavQueryVariables
  >(graphNavQuery, {
    variables: {
      id: graphRef?.graphId as string,
      graphVariant: graphRef?.graphVariant as string,
    },
    skip: !graphRef,
    context: appLinkContext({
      catchErrors: [ignorePermissionsErrors],
    }),
  });

  const hasManagedSubgraphs = Boolean(
    data?.service?.variant?.hasManagedSubgraphs,
  );
  const isContractVariant = Boolean(data?.service?.variant?.isContract);
  const graphIsManaged = hasManagedSubgraphs || isContractVariant;

  const isCloudSupergraph =
    data?.service?.graphType === GraphType.CLOUD_SUPERGRAPH;

  const shouldShowVariantLevelPersistedQueries = useLDFlag(
    'gov-pq-ux-sort-filter-delete',
  );

  const canAccessLaunches = currentPlanResult.currentPlan?.launches ?? false;
  const capabilities = useCapabilities().capabilities;
  const canAccessProposals = capabilities?.proposals;
  const canAccessPersistedQueries =
    shouldShowVariantLevelPersistedQueries && capabilities?.persistedQueries;

  const {
    loading: loadingPermissions,
    data: { graphPermissions },
  } = usePermissions(undefined, graphRef?.graphId, graphRef?.graphVariant);

  const graphVisibilityType = useGraphVisibility();
  const { locationToSchema, locationToExplorer, isExplorer } =
    useInternalGraphLinking();

  const isPublicRoute = graphVisibilityType === 'public';
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;

  if (error || currentPlanResult.error) {
    console.error(error || currentPlanResult.error); // eslint-disable-line no-console
  }

  // When a user doesn't have permission to a graph at all, we want to show the
  // nav undisabled and allow them to click through to see the full page blocks
  // All roles that have access to any information will have canQueryPrivateInfo,
  // BILLING_MANAGERS and users not in the org do not have access to any information
  const shouldDisableNavItems = graphPermissions?.canQueryPrivateInfo;

  const [hasRunFirstQuery] = useLocalStorage('hasRunFirstQuery');
  const [hasDismissedReadMeNudge, setHasDismissedReadMeNudge] = useLocalStorage(
    'hasDismissedReadMeNudge',
  );

  const [readmeNudgeDelayPassed, setReadmeNudgeDelayPassed] = useState(false);
  useEffect(() => {
    if (isCloudSupergraph && hasRunFirstQuery && !hasDismissedReadMeNudge) {
      const timeout = setTimeout(() => setReadmeNudgeDelayPassed(true), 1000);
      return () => clearTimeout(timeout);
    }
  }, [hasDismissedReadMeNudge, hasRunFirstQuery, isCloudSupergraph]);
  const onCloseReadmeNudge = useCallback(() => {
    setHasDismissedReadMeNudge(true);
  }, [setHasDismissedReadMeNudge]);

  const shouldDisableInsights =
    shouldDisableNavItems &&
    !(graphPermissions?.canQueryStats && graphPermissions?.canQueryTraces);

  const shouldNudgeReadme =
    isCloudSupergraph &&
    readmeNudgeDelayPassed &&
    !!hasRunFirstQuery &&
    !hasDismissedReadMeNudge;

  return (
    <WrapMaybe
      element={
        /**
         * TODO(dark-mode)
         * We need this here so that the GraphNav can mirror the Explorer theme
         * when the app is on the Explorer page (light or dark).
         */
        <ThemeProvider className="h-full flex-none bg-secondary-inverted text-sm">
          <></>
        </ThemeProvider>
      }
      on={isExplorer}
    >
      <header
        className="flex h-full flex-col flex-nowrap justify-between gap-2 overflow-auto border-r border-primary bg-primary p-2"
        aria-label="Graph level navigation"
      >
        {loading || loadingPermissions ? (
          <div className="mt-3 flex justify-center">
            <Spinner size="tiny" />
          </div>
        ) : (
          <>
            {graphRef && !isSandboxRoute && (
              <InformationalNudge
                isOpen={shouldNudgeReadme}
                className="z-50"
                placement="right-end"
                onClose={onCloseReadmeNudge}
                title="What’s next?"
                description="Head over to your README to learn more about Studio’s features, give your team insight into your graph, and more!"
              >
                <CollapsibleNavItem
                  collapsed={collapsed}
                  to={variantHomepageRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: variantRouteConfig,
                    patch: { tab: RouteConfig.DELETE },
                  })}
                  icon={IconTelescope4}
                  label="Overview"
                />
              </InformationalNudge>
            )}

            {isCloudSupergraph && (
              <CollapsibleNavItem
                collapsed={collapsed}
                to={cloudSettingsRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: variantRouteConfig,
                })}
                label="Cloud Router"
                icon={IconCloud}
              />
            )}

            <CollapsibleNavItem
              collapsed={collapsed}
              to={locationToSchema({})}
              icon={IconSchema}
              label="Schema"
            />

            {graphRef &&
              !isPublicRoute &&
              canAccessProposals &&
              !isContractVariant &&
              hasManagedSubgraphs && (
                <CollapsibleNavItem
                  collapsed={collapsed}
                  disabled={
                    shouldDisableNavItems && !graphPermissions.canQueryProposals
                  }
                  to={variantProposalListRouteConfig.locationFrom({
                    location,
                    patch: {
                      view: 'variant',
                      filterByStatuses: [
                        GraphQLTypes.ProposalStatus.OPEN,
                        GraphQLTypes.ProposalStatus.DRAFT,
                        GraphQLTypes.ProposalStatus.APPROVED,
                      ],
                    },
                    fromRouteConfig: variantRouteConfig,
                  })}
                  extraActiveRoutes={[variantProposalListRouteConfig]}
                  icon={IconProposals}
                  label="Proposals"
                />
              )}

            <CollapsibleNavItem
              collapsed={collapsed}
              to={locationToExplorer({})}
              icon={IconExplorer}
              label="Explorer"
            />

            {graphRef && !isPublicRoute && canAccessPersistedQueries && (
              <CollapsibleNavItem
                disabled={
                  shouldDisableNavItems &&
                  !graphPermissions.canQueryPersistedQueryLists
                }
                collapsed={collapsed}
                to={variantPersistedQueriesRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: variantRouteConfig,
                })}
                // TODO(pq-ux): This icon will be updated once the design is finalized
                icon={IconBulkEdit}
                label="Persisted Queries"
              />
            )}

            {isSandboxRoute && (
              <CollapsibleNavItem
                collapsed={collapsed}
                to={sandboxDiffRouteConfig.location({})}
                icon={IconDiff}
                label="Schema Diff"
              />
            )}
            {isSandboxRoute && (
              <CollapsibleNavItem
                collapsed={collapsed}
                to={sandboxCheckRouteConfig.location({})}
                icon={IconCheck}
                label="Checks"
              />
            )}
            {graphRef && (
              <CollapsibleNavItem
                collapsed={collapsed}
                to={changelogRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: variantRouteConfig,
                })}
                icon={IconDiff}
                label="Changelog"
              />
            )}

            {graphRef && !isPublicRoute && hasManagedSubgraphs && (
              <CollapsibleNavItem
                disabled={
                  shouldDisableNavItems &&
                  !graphPermissions?.canQueryImplementingServices
                }
                collapsed={collapsed}
                to={subgraphsRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: variantRouteConfig,
                })}
                icon={IconServices}
                label="Subgraphs"
              />
            )}

            {graphRef && !isPublicRoute && (
              <>
                <CollapsibleNavItem
                  disabled={
                    shouldDisableNavItems && !graphPermissions?.canQueryStats
                  }
                  collapsed={collapsed}
                  to={clientsRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: variantRouteConfig,
                    patch: { fastModeRange: RouteConfig.DELETE },
                  })}
                  icon={IconClients}
                  label="Clients"
                />

                <CollapsibleNavItem
                  disabled={shouldDisableInsights}
                  collapsed={collapsed}
                  to={insightsRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: variantRouteConfig,
                  })}
                  icon={IconMetrics}
                  label="Insights"
                />

                <CollapsibleNavItem
                  disabled={
                    shouldDisableNavItems && !graphPermissions?.canCheckSchemas
                  }
                  collapsed={collapsed}
                  to={checksRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: variantRouteConfig,
                    patch: {
                      view: 'variant',
                    },
                  })}
                  extraActiveRoutes={[checksRouteConfig, checkRouteConfig]}
                  icon={IconCheck}
                  label="Checks"
                />

                {canAccessLaunches && graphIsManaged && (
                  <CollapsibleNavItem
                    disabled={
                      shouldDisableNavItems &&
                      !graphPermissions?.canCheckSchemas
                    }
                    collapsed={collapsed}
                    to={launchesRouteConfig.locationFrom({
                      location,
                      fromRouteConfig: variantRouteConfig,
                    })}
                    icon={IconRocket}
                    label="Launches"
                  />
                )}
              </>
            )}

            {graphRef && !isPublicRoute && (
              <CollapsibleNavItem
                disabled={
                  shouldDisableNavItems &&
                  !graphPermissions?.canQueryTokens &&
                  !graphPermissions?.canUpdateTitle &&
                  !graphPermissions?.canUpdateAvatar
                }
                collapsed={collapsed}
                to={variantSettingsRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: variantRouteConfig,
                })}
                icon={IconSettings}
                label="Settings"
                extraActiveRoutes={[graphSettingsRouteConfig]}
              />
            )}

            {(error || currentPlanResult.error) && (
              <Tooltip
                label="Hmm, something went wrong when trying to load the navigation. Please try reloading your browser."
                placement="right"
              >
                <IconWarning className="mx-3 text-icon-error" />
              </Tooltip>
            )}

            <div className="flex-1" />

            <CollapsibleNavItem
              collapsed={collapsed}
              onClick={() => {
                setCollapsed((previousValue) => !previousValue);
              }}
              icon={collapsed ? IconDoubleChevronRight : IconDoubleChevronLeft}
              tertiary={true}
              label={collapsed ? 'Expand' : 'Collapse'}
            />
          </>
        )}
      </header>
    </WrapMaybe>
  );
};
